/**
 * Shared Sync Operations
 *
 * This module provides common sync operations that can be used
 * by both the main application and the service worker.
 */

import {
  logAnalytics,
  getSyncQueue,
  removeFromSyncQueue,
  updateSyncQueueItem,
  getAuthStatus,
  openDB,
} from "./dbOperations";
import googleSheetsService from "../../services/googleSheetsService";
import { OFFLINE_CONFIG } from "../../config";

// Maximum number of retry attempts for failed sync operations
export const MAX_RETRY_ATTEMPTS = 5;

// Exponential backoff delay calculation (in milliseconds)
export const getRetryDelay = (retryCount) => {
  return Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30 seconds
};

// Store a flag indicating no eligible items in the sync queue
const storeNoEligibleItemsFlag = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction("syncQueue", "readwrite");
    const store = tx.objectStore("syncQueue");

    return new Promise((resolve, reject) => {
      const request = store.put({
        id: "noEligibleItemsFlag",
        timestamp: new Date().toISOString(),
      });

      request.onsuccess = () => resolve(true);

      tx.onerror = (event) => {
        console.error(
          "Error storing no eligible items flag:",
          event.target.error
        );
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error storing no eligible items flag:", error);
    return false;
  }
};

// Clear the no eligible items flag
export const clearNoEligibleItemsFlag = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction("syncQueue", "readwrite");
    const store = tx.objectStore("syncQueue");

    return new Promise((resolve, reject) => {
      const request = store.delete("noEligibleItemsFlag");

      request.onsuccess = () => resolve(true);

      tx.onerror = (event) => {
        console.error(
          "Error clearing no eligible items flag:",
          event.target.error
        );
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error clearing no eligible items flag:", error);
    return false;
  }
};

// Check if there are no eligible items flag
export const hasNoEligibleItemsFlag = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction("syncQueue", "readonly");
    const store = tx.objectStore("syncQueue");

    return new Promise((resolve, reject) => {
      const request = store.get("noEligibleItemsFlag");

      request.onsuccess = (event) => {
        resolve(!!event.target.result);
      };

      tx.onerror = (event) => {
        console.error(
          "Error checking no eligible items flag:",
          event.target.error
        );
        reject(event.target.error);
      };
    });
  } catch (error) {
    console.error("Error checking no eligible items flag:", error);
    return false;
  }
};

// Function to check if there are eligible items in the sync queue
export const hasEligibleSyncItems = async () => {
  try {
    const allItems = await getSyncQueue();
    // Filter out items with 'failed' status
    const eligibleItems = allItems.filter(
      (item) => item.status !== "failed" && item.id !== "noEligibleItemsFlag"
    );
    return eligibleItems.length > 0;
  } catch (error) {
    console.error("Error checking eligible sync items:", error);
    return false;
  }
};

// Function to sync categorizations from IndexedDB to server
export const syncCategorizations = async (batchSize = 10) => {
  try {
    // Skip if offline mode is disabled
    if (!OFFLINE_CONFIG.enabled) {
      console.log("Offline mode is disabled, skipping sync");
      
      // Log the offline mode status
      await logAnalytics({
        eventType: "syncSkipped",
        reason: "offlineModeDisabled",
        timestamp: new Date().toISOString(),
      });
      
      return {
        success: false,
        reason: "offline_mode_disabled",
        message: "Offline mode is disabled, sync operations are not available",
      };
    }
    
    // Determine if we're in a service worker context
    const isServiceWorker = typeof window === "undefined";

    // If in service worker, check authentication status
    if (isServiceWorker) {
      const isAuthenticated = await getAuthStatus();

      // If not authenticated in service worker, don't process the queue
      if (!isAuthenticated) {
        console.log("Not authenticated in service worker, skipping sync");

        // Log the authentication status
        await logAnalytics({
          eventType: "authRequiredForSync",
          timestamp: new Date().toISOString(),
        });

        return {
          success: false,
          reason: "authentication_required",
          message:
            "Authentication required to process sync queue in service worker",
        };
      }
    }

    // Log sync start
    await logAnalytics({
      eventType: "syncStart",
      timestamp: new Date().toISOString(),
    });

    // Get items from the sync queue (limit to batch size to avoid overwhelming the network)
    const allItems = await getSyncQueue();

    // Filter out items with 'failed' status to prevent infinite retry loops
    const eligibleItems = allItems.filter((item) => item.status !== "failed");

    // Apply batch size limit
    const queue = eligibleItems.slice(0, batchSize);

    if (queue.length === 0) {
      console.log("No eligible items in sync queue");

      // Store a flag indicating no eligible items
      await storeNoEligibleItemsFlag();

      // Log empty queue
      await logAnalytics({
        eventType: "syncComplete",
        processed: 0,
        remaining: 0,
        failed: allItems.length - eligibleItems.length,
        timestamp: new Date().toISOString(),
      });

      return {
        success: true,
        processed: 0,
        remaining: 0,
        failed: allItems.length - eligibleItems.length,
        noEligibleItems: true,
      };
    }

    console.log(`Processing ${queue.length} items in sync queue`);

    let processed = 0;
    let failed = 0;

    // Process each item in the queue
    for (const item of queue) {
      try {
        // Check if we should retry based on retry count
        if (item.retryCount >= MAX_RETRY_ATTEMPTS) {
          console.warn(
            `Queue item ${item.id} exceeded maximum retry attempts, marking as failed`
          );

          // Update item status to failed
          await updateSyncQueueItem(item.id, {
            status: "failed",
            lastAttempt: new Date().toISOString(),
          });

          // Log permanent failure
          await logAnalytics({
            eventType: "syncItemFailed",
            itemId: item.id,
            retryCount: item.retryCount,
            timestamp: new Date().toISOString(),
          });

          failed++;
          continue;
        }

        // Update Google Sheet with the category
        // The rowId is 1-based in the database but 0-based for the Google Sheets API
        const rowIndex = parseInt(item.rowId) - 1;
        await googleSheetsService.updateSheetWithCategory(rowIndex, item.categoryId);

        // Remove from queue if successful
        await removeFromSyncQueue(item.id);

        processed++;

        // Log success
        await logAnalytics({
          eventType: "syncItemSuccess",
          itemId: item.id,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error(`Error processing queue item ${item.id}:`, error);

        // Update retry count and apply exponential backoff
        const retryCount = (item.retryCount || 0) + 1;
        const nextRetry = new Date(Date.now() + getRetryDelay(retryCount));

        await updateSyncQueueItem(item.id, {
          status: "retry",
          retryCount,
          lastAttempt: new Date().toISOString(),
          nextRetry: nextRetry.toISOString(),
          lastError: error.message,
        });

        // Log retry
        await logAnalytics({
          eventType: "syncItemRetry",
          itemId: item.id,
          retryCount,
          nextRetry: nextRetry.toISOString(),
          errorMessage: error.message,
          timestamp: new Date().toISOString(),
        });
      }
    }

    // Get remaining count (excluding failed items)
    const allRemainingItems = await getSyncQueue();
    const remainingEligibleItems = allRemainingItems.filter(
      (item) => item.status !== "failed"
    );
    const remaining = remainingEligibleItems.length;
    const totalFailed = allRemainingItems.filter(
      (item) => item.status === "failed"
    ).length;

    // Log sync completion
    await logAnalytics({
      eventType: "syncComplete",
      processed,
      failed,
      remaining,
      totalFailed,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      processed,
      failed,
      remaining,
      totalFailed,
    };
  } catch (error) {
    console.error("Error processing sync queue:", error);

    // Log sync error
    await logAnalytics({
      eventType: "syncError",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
};

// Function to process items that are ready for retry
export const processRetryItems = async () => {
  try {
    // Skip if offline mode is disabled
    if (!OFFLINE_CONFIG.enabled) {
      console.log("Offline mode is disabled, skipping retry processing");
      
      // Log the offline mode status
      await logAnalytics({
        eventType: "retrySkipped",
        reason: "offlineModeDisabled",
        timestamp: new Date().toISOString(),
      });
      
      return {
        success: false,
        reason: "offline_mode_disabled",
        message: "Offline mode is disabled, retry operations are not available",
      };
    }
    
    // Determine if we're in a service worker context
    const isServiceWorker = typeof window === "undefined";

    // If in service worker, check authentication status
    if (isServiceWorker) {
      const isAuthenticated = await getAuthStatus();

      // If not authenticated in service worker, don't process retry items
      if (!isAuthenticated) {
        console.log(
          "Not authenticated in service worker, skipping retry processing"
        );

        // Log the authentication status
        await logAnalytics({
          eventType: "authRequiredForRetry",
          timestamp: new Date().toISOString(),
        });

        return {
          success: false,
          reason: "authentication_required",
          message:
            "Authentication required to process retry items in service worker",
        };
      }
    }

    const now = new Date().toISOString();
    const queue = await getSyncQueue();

    // Filter items that are ready for retry
    const retryItems = queue.filter(
      (item) =>
        item.status === "retry" && item.nextRetry && item.nextRetry <= now
    );

    if (retryItems.length === 0) {
      return { success: true, retried: 0 };
    }

    console.log(`Processing ${retryItems.length} retry items`);

    // Log retry start
    await logAnalytics({
      eventType: "retryStart",
      itemCount: retryItems.length,
      timestamp: new Date().toISOString(),
    });

    // Process each retry item by triggering a sync
    return await syncCategorizations(retryItems.length);
  } catch (error) {
    console.error("Error processing retry items:", error);

    // Log error
    await logAnalytics({
      eventType: "retryError",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
};

// Function to send a categorization directly to Google Sheets
export const sendCategorization = async (categorization) => {
  try {
    // Log the direct categorization attempt
    await logAnalytics({
      eventType: "directCategorization",
      rowId: categorization.rowId,
      categoryId: categorization.categoryId,
      timestamp: new Date().toISOString(),
    });

    // The rowId is 1-based in the database but 0-based for the Google Sheets API
    const rowIndex = parseInt(categorization.rowId) - 1;
    
    // Update the Google Sheet with the category
    await googleSheetsService.updateSheetWithCategory(rowIndex, categorization.categoryId);

    // Log success
    await logAnalytics({
      eventType: "directCategorizationSuccess",
      rowId: categorization.rowId,
      categoryId: categorization.categoryId,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      rowId: categorization.rowId,
      categoryId: categorization.categoryId,
    };
  } catch (error) {
    console.error("Error sending categorization:", error);

    // Log error
    await logAnalytics({
      eventType: "directCategorizationError",
      rowId: categorization.rowId,
      categoryId: categorization.categoryId,
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
};

export default {
  MAX_RETRY_ATTEMPTS,
  getRetryDelay,
  syncCategorizations,
  processRetryItems,
  clearNoEligibleItemsFlag,
  hasNoEligibleItemsFlag,
  hasEligibleSyncItems,
  sendCategorization,
};
